import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  Loader2, UserPlus, Mail, Copy, Trash2, ShieldCheck, Crown, User, Activity, Check,
} from "lucide-react";
import { AppShell } from "@/components/app/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { EmptyState } from "@/components/common/EmptyState";
import api, { getErrorMessage } from "@/lib/api";

const ROLE_BADGE = {
  owner: { label: "Owner", icon: Crown, cls: "bg-primary/10 text-primary" },
  admin: { label: "Admin", icon: ShieldCheck, cls: "bg-[hsl(var(--risk-medium-bg))] text-[hsl(var(--risk-medium))]" },
  member: { label: "Member", icon: User, cls: "bg-secondary text-secondary-foreground" },
};

const RoleBadge = ({ role }) => {
  const r = ROLE_BADGE[role] || ROLE_BADGE.member;
  const Icon = r.icon;
  return <Badge className={`gap-1 ${r.cls}`}><Icon className="h-3 w-3" />{r.label}</Badge>;
};

const inviteLink = (token) => `${window.location.origin}/accept-invite?token=${token}`;

export default function Team() {
  const [data, setData] = useState(null);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("member");
  const [inviting, setInviting] = useState(false);
  const [copied, setCopied] = useState(null);

  const load = useCallback(async () => {
    try {
      const [t, a] = await Promise.all([api.get("/team"), api.get("/team/activity")]);
      setData(t.data);
      setActivity(a.data.activity || []);
    } catch (e) {
      toast.error(getErrorMessage(e, "Could not load team."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const invite = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setInviting(true);
    try {
      const r = await api.post("/team/invite", { email: email.trim(), role });
      toast.success(`Invite created for ${r.data.email}`);
      setEmail("");
      await load();
      // Auto-copy the invite link for convenience
      try {
        await navigator.clipboard.writeText(inviteLink(r.data.invite_token));
        toast.success("Invite link copied to clipboard");
      } catch (_) { /* clipboard may be blocked */ }
    } catch (err) {
      toast.error(getErrorMessage(err, "Could not send invite."));
    } finally {
      setInviting(false);
    }
  };

  const copyLink = async (token) => {
    try {
      await navigator.clipboard.writeText(inviteLink(token));
      setCopied(token);
      setTimeout(() => setCopied(null), 2000);
      toast.success("Invite link copied");
    } catch (_) {
      toast.error("Could not copy link");
    }
  };

  const changeRole = async (id, newRole) => {
    try {
      await api.post(`/team/${id}/role`, { role: newRole });
      toast.success("Role updated");
      load();
    } catch (e) {
      toast.error(getErrorMessage(e, "Could not change role."));
    }
  };

  const remove = async (id, label) => {
    try {
      await api.delete(`/team/${id}`);
      toast.success(`Removed ${label}`);
      load();
    } catch (e) {
      toast.error(getErrorMessage(e, "Could not remove member."));
    }
  };

  if (loading) {
    return <AppShell title="Team"><div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div></AppShell>;
  }

  const canManage = data?.can_manage;
  const isOwner = data?.role === "owner";

  return (
    <AppShell title="Team Management">
      <div className="space-y-6" data-testid="team-page">
        <p className="text-sm text-muted-foreground">Invite colleagues, assign roles and manage who can access your portfolio.</p>

        {/* Invite form */}
        {canManage && (
          <form onSubmit={invite} className="rounded-xl border bg-card p-5" data-testid="team-invite-form">
            <div className="mb-3 flex items-center gap-2">
              <UserPlus className="h-4 w-4 text-primary" />
              <h3 className="font-display text-base font-semibold">Invite a team member</h3>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row">
              <div className="relative flex-1">
                <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="colleague@email.com" className="pl-9" data-testid="team-invite-email" />
              </div>
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger className="w-full sm:w-40" data-testid="team-invite-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="member">Member</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
              <Button type="submit" disabled={inviting} className="gap-2" data-testid="team-invite-submit">
                {inviting ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />} Send invite
              </Button>
            </div>
            <p className="mt-2 text-xs text-muted-foreground">An invite link is generated and copied to your clipboard — share it with your colleague to join.</p>
          </form>
        )}

        {/* Members */}
        <div className="rounded-xl border bg-card" data-testid="team-members">
          <div className="border-b px-5 py-4"><h3 className="font-display text-base font-semibold">Team members</h3></div>
          <div className="divide-y">
            {data?.owner && (
              <div className="flex items-center justify-between px-5 py-3.5">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium">{data.owner.business_name || data.owner.email}</p>
                  <p className="text-xs text-muted-foreground">{data.owner.email}</p>
                </div>
                <RoleBadge role="owner" />
              </div>
            )}
            {data?.members?.map((m) => (
              <div key={m.id} className="flex items-center justify-between gap-3 px-5 py-3.5" data-testid={`team-member-${m.id}`}>
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium">{m.email}</p>
                  <p className="text-xs text-muted-foreground">Joined {m.accepted_at ? new Date(m.accepted_at).toLocaleDateString() : "—"}</p>
                </div>
                <div className="flex items-center gap-2">
                  {isOwner ? (
                    <Select value={m.role} onValueChange={(v) => changeRole(m.id, v)}>
                      <SelectTrigger className="h-8 w-28" data-testid={`team-role-${m.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="member">Member</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : <RoleBadge role={m.role} />}
                  {canManage && (
                    <Button variant="ghost" size="icon" onClick={() => remove(m.id, m.email)} title="Remove" data-testid={`team-remove-${m.id}`}>
                      <Trash2 className="h-4 w-4 text-muted-foreground" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
            {(!data?.members || data.members.length === 0) && (
              <p className="px-5 py-6 text-sm text-muted-foreground">No team members yet. Invite your first colleague above.</p>
            )}
          </div>
        </div>

        {/* Pending invites */}
        {data?.pending?.length > 0 && (
          <div className="rounded-xl border bg-card" data-testid="team-pending">
            <div className="border-b px-5 py-4"><h3 className="font-display text-base font-semibold">Pending invites</h3></div>
            <div className="divide-y">
              {data.pending.map((p) => (
                <div key={p.id} className="flex items-center justify-between gap-3 px-5 py-3.5" data-testid={`team-pending-${p.id}`}>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium">{p.email}</p>
                    <p className="text-xs text-muted-foreground">Invited {p.invited_at ? new Date(p.invited_at).toLocaleDateString() : ""} · <RoleBadge role={p.role} /></p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" className="gap-1.5" onClick={() => copyLink(p.invite_token)} data-testid={`team-copy-${p.id}`}>
                      {copied === p.invite_token ? <Check className="h-3.5 w-3.5 text-[hsl(var(--risk-low))]" /> : <Copy className="h-3.5 w-3.5" />} Copy link
                    </Button>
                    {canManage && (
                      <Button variant="ghost" size="icon" onClick={() => remove(p.id, p.email)} title="Revoke" data-testid={`team-revoke-${p.id}`}>
                        <Trash2 className="h-4 w-4 text-muted-foreground" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Activity log */}
        {canManage && (
          <div className="rounded-xl border bg-card" data-testid="team-activity">
            <div className="flex items-center gap-2 border-b px-5 py-4">
              <Activity className="h-4 w-4 text-primary" />
              <h3 className="font-display text-base font-semibold">Team activity</h3>
            </div>
            {activity.length === 0 ? (
              <p className="px-5 py-6 text-sm text-muted-foreground">No activity yet.</p>
            ) : (
              <div className="max-h-80 divide-y overflow-y-auto">
                {activity.map((a) => (
                  <div key={a.id} className="px-5 py-3 text-sm">
                    <span className="font-medium capitalize">{a.action.replace("_", " ")}</span>
                    {a.detail ? <span className="text-muted-foreground"> · {a.detail}</span> : null}
                    <p className="text-xs text-muted-foreground">{a.actor_email} · {new Date(a.created_at).toLocaleString()}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </AppShell>
  );
}
